import { type PrimitiveAtom, atom, useAtom } from 'jotai';
import { useCallback } from 'react';
import type { EstimateServiceBreakdown } from '../components/pricing/SeasonalPricing/utils/estimateDisplay';
import type {
	EstimateSourceType,
	PricingEstimationService,
} from '../components/pricing/types';

export const PERSISTED_ESTIMATE_KEY_PREFIX = 'app:TKMarketplace:Estimate:';
export const CURRENT_VERSION = 7;

const VALID_PRICING_SERVICES = new Set<PricingEstimationService>([
	'ceremony',
	'reception',
	'catering',
	'barServices',
]);

const isValidUnavailableServices = (
	value: unknown,
): value is PricingEstimationService[] => {
	if (!Array.isArray(value)) return false;
	for (const s of value) {
		if (typeof s !== 'string') return false;
		if (!VALID_PRICING_SERVICES.has(s as PricingEstimationService))
			return false;
	}
	return true;
};

type WeddingDate =
	| {
			type: 'exact';
			date: string;
	  }
	| {
			type: 'month-year';
			month: string;
			year: number;
	  };

type PersistedEstimateTotal = {
	min: number;
	max: number;
	isBelowMinimumSpend?: boolean;
	originalMinCost?: number;
};

export interface PersistedEstimate {
	version?: number;
	storefrontId: string;
	guestCount: number;
	weddingDate: WeddingDate;
	selections: Record<string, unknown>;
	calculatedTotal: PersistedEstimateTotal;
	estimateSourceType?: EstimateSourceType;
	serviceBreakdown?: EstimateServiceBreakdown;
	unavailableServices?: PricingEstimationService[];
	lastUpdated?: string;
}

interface UsePersistedEstimateResult {
	estimate: PersistedEstimate | null;
	setEstimate: (estimate: PersistedEstimate) => void;
	clearEstimate: () => void;
}

const getStorageKey = (storefrontId: string) =>
	`${PERSISTED_ESTIMATE_KEY_PREFIX}${storefrontId}:v${CURRENT_VERSION}`;

const isValidWeddingDate = (wd: WeddingDate): boolean => {
	if (wd.type === 'exact') return typeof wd.date === 'string';
	return (
		typeof wd.month === 'string' &&
		typeof wd.year === 'number' &&
		Number.isFinite(wd.year) &&
		Number.isInteger(wd.year)
	);
};

const isValidPersistedEstimate = (
	value: PersistedEstimate,
): value is PersistedEstimate => {
	if (typeof value !== 'object' || value === null) return false;

	const v = value;

	if (v.version !== CURRENT_VERSION) return false;

	if (typeof v.storefrontId !== 'string') return false;

	if (
		typeof v.guestCount !== 'number' ||
		!Number.isFinite(v.guestCount) ||
		!Number.isInteger(v.guestCount) ||
		v.guestCount < 0
	)
		return false;

	if (typeof v.lastUpdated !== 'string') return false;

	if (v.weddingDate === undefined || !isValidWeddingDate(v.weddingDate))
		return false;

	const ct = v.calculatedTotal;
	if (typeof ct !== 'object' || ct === null) return false;

	if (
		typeof ct.min !== 'number' ||
		typeof ct.max !== 'number' ||
		!Number.isFinite(ct.min) ||
		!Number.isFinite(ct.max) ||
		ct.min > ct.max
	)
		return false;

	if (
		v.selections === null ||
		typeof v.selections !== 'object' ||
		Array.isArray(v.selections)
	)
		return false;

	if (
		v.estimateSourceType !== undefined &&
		v.estimateSourceType !== 'vendor' &&
		v.estimateSourceType !== 'market' &&
		v.estimateSourceType !== 'mix'
	)
		return false;

	if (v.serviceBreakdown !== undefined) {
		const sb = v.serviceBreakdown;
		if (sb === null || typeof sb !== 'object' || Array.isArray(sb))
			return false;

		for (const val of Object.values(sb)) {
			if (typeof val === 'string') continue;
			if (typeof val !== 'object' || val === null || Array.isArray(val))
				return false;
			if (!('price' in val)) {
				if ('unavailable' in val && val.unavailable === true) continue;
				return false;
			}
			if (typeof val.price !== 'string') return false;
			if (val.isFree !== undefined && typeof val.isFree !== 'boolean')
				return false;
			if (
				val.sourceType !== null &&
				val.sourceType !== 'VENDOR' &&
				val.sourceType !== 'MARKET' &&
				val.sourceType !== 'LOCAL_MARKET' &&
				val.sourceType !== 'AI'
			)
				return false;
		}
	}

	if (
		v.unavailableServices !== undefined &&
		!isValidUnavailableServices(v.unavailableServices)
	)
		return false;

	return true;
};

const getStorage = (): Storage | null => {
	if (typeof window === 'undefined') return null;
	return window.localStorage ?? null;
};

export const getEstimate = (storefrontId: string): PersistedEstimate | null => {
	const key = getStorageKey(storefrontId);

	try {
		const storage = getStorage();
		if (!storage) return null;

		const raw = storage.getItem(key);
		if (!raw) return null;

		const parsed: PersistedEstimate = JSON.parse(raw);
		if (!isValidPersistedEstimate(parsed)) {
			// Clear out invalid or outdated data to avoid downstream crashes.
			storage.removeItem(key);
			return null;
		}

		// Treat mismatched storefront IDs as invalid and clear the entry.
		if (parsed.storefrontId !== storefrontId) {
			storage.removeItem(key);
			return null;
		}

		return parsed;
	} catch (e) {
		// If reading/parsing fails, clear the stored value to self-heal corrupted entries.
		try {
			const storage = getStorage();
			if (storage) storage.removeItem(key);
		} catch {
			// Ignore secondary errors while trying to clean up localStorage.
		}
		console.warn('Could not read estimate from localStorage', e);
		return null;
	}
};

// Shared, storefrontId-scoped atoms so every `usePersistedEstimate(storefrontId)`
// instance (e.g. `EstimateFormModal` and `EditEstimateFormModal`) observes the
// same live value, instead of each hook call holding its own local `useState`.
// One atom is created per distinct storefrontId and cached for the lifetime of
// the page (a page only ever deals with a single storefront in practice).
const persistedEstimateAtomCache = new Map<
	string,
	PrimitiveAtom<PersistedEstimate | null>
>();

const getPersistedEstimateAtom = (storefrontId: string) => {
	const cached = persistedEstimateAtomCache.get(storefrontId);
	if (cached) return cached;

	const newAtom = atom<PersistedEstimate | null>(getEstimate(storefrontId));
	persistedEstimateAtomCache.set(storefrontId, newAtom);
	return newAtom;
};

// Exported only so tests can reset the cache between test cases.
export const resetPersistedEstimateAtomCache = () => {
	persistedEstimateAtomCache.clear();
};

export const usePersistedEstimate = (
	storefrontId: string,
): UsePersistedEstimateResult => {
	const [estimate, setEstimateState] = useAtom(
		getPersistedEstimateAtom(storefrontId),
	);

	const setEstimate = useCallback(
		(newEstimate: PersistedEstimate) => {
			if (newEstimate.storefrontId !== storefrontId) {
				console.warn(
					'storefrontId mismatch in usePersistedEstimate.setEstimate; overriding estimate.storefrontId to match hook storefrontId.',
					{
						hookStorefrontId: storefrontId,
						estimateStorefrontId: newEstimate.storefrontId,
					},
				);
			}

			// Always normalize persistence metadata so the stored entry can always
			// be read back by this hook, regardless of what the caller provided.
			const normalizedEstimate: PersistedEstimate = {
				...newEstimate,
				storefrontId,
				version: CURRENT_VERSION,
				lastUpdated: new Date().toISOString(),
			};

			// Always update in-memory state; treat localStorage as best-effort.
			setEstimateState(normalizedEstimate);

			try {
				const storage = getStorage();
				if (storage) {
					storage.setItem(
						getStorageKey(storefrontId),
						JSON.stringify(normalizedEstimate),
					);
				}
			} catch (e) {
				console.warn('Could not save estimate to localStorage', e);
			}
		},
		[storefrontId, setEstimateState],
	);

	const clearEstimate = useCallback(() => {
		// Always update in-memory state; treat localStorage as best-effort.
		setEstimateState(null);

		try {
			const storage = getStorage();
			if (storage) {
				storage.removeItem(getStorageKey(storefrontId));
			}
		} catch (e) {
			console.warn('Could not clear estimate from localStorage', e);
		}
	}, [storefrontId, setEstimateState]);

	return { estimate, setEstimate, clearEstimate };
};
